/* ============================================================================
 * pwaRegister.js
 *
 * Registers the service worker exactly once per page-load on production
 * builds, and exposes a small event bus so the UI can react to:
 *   - "install-available"  (the browser fired `beforeinstallprompt`)
 *   - "app-installed"      (the browser dispatched `appinstalled`)
 *   - "sw-waiting"         (a new SW version is ready to take over)
 *
 * Keeping this separate from the React tree lets us subscribe from any
 * component without prop-drilling.
 * ========================================================================== */

const listeners = new Map(); // event -> Set<handler>
let deferredInstallPrompt = null;

export const PWA_EVENTS = {
  INSTALL_AVAILABLE: 'install-available',
  APP_INSTALLED: 'app-installed',
  SW_WAITING: 'sw-waiting',
};

export const onPwaEvent = (event, handler) => {
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event).add(handler);
  return () => listeners.get(event)?.delete(handler);
};

const emit = (event, payload) => {
  const set = listeners.get(event);
  if (!set) return;
  for (const handler of set) {
    try {
      handler(payload);
    } catch (_) {
      /* ignore */
    }
  }
};

/** Returns true when running as a standalone PWA (not in a browser tab). */
export const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  if (typeof navigator !== 'undefined' && navigator.standalone === true) return true;
  return false;
};

/** iOS Safari never fires beforeinstallprompt — callers need to show
 *  manual "add to home screen" instructions instead. */
export const isIosSafari = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const webkit = /WebKit/i.test(ua);
  const chromeOrFirefox = /(CriOS|FxiOS|OPiOS|mercury)/i.test(ua);
  return iOS && webkit && !chromeOrFirefox;
};

export const getDeferredPrompt = () => deferredInstallPrompt;

/** Trigger the native install dialog. Resolves to "accepted"/"dismissed"/null. */
export const triggerInstall = async () => {
  if (!deferredInstallPrompt) return null;
  try {
    deferredInstallPrompt.prompt();
    const result = await deferredInstallPrompt.userChoice;
    deferredInstallPrompt = null;
    return result?.outcome || null;
  } catch (_) {
    deferredInstallPrompt = null;
    return null;
  }
};

/** Register the service worker and wire up install lifecycle listeners.
 *  Safe to call multiple times (subsequent calls are no-ops).          */
let wired = false;
export const registerServiceWorker = () => {
  if (wired) return;
  wired = true;

  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    emit(PWA_EVENTS.INSTALL_AVAILABLE);
  });

  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    emit(PWA_EVENTS.APP_INSTALLED);
  });

  // Only register in production builds — CRA dev server doesn't like SWs.
  const isProd = process.env.NODE_ENV === 'production';
  if (!isProd) return;
  if (!('serviceWorker' in navigator)) return;

  // When a newly-installed SW takes control of this page, reload once so the
  // page actually runs the new bundle. Without this the browser (especially on
  // mobile, where pages are restored from memory) keeps executing the old code
  // even after a new version is deployed and activated. Guarded against loops.
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js', { scope: '/' })
      .then((registration) => {
        if (!registration) return;
        registration.onupdatefound = () => {
          const worker = registration.installing;
          if (!worker) return;
          worker.onstatechange = () => {
            if (
              worker.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              emit(PWA_EVENTS.SW_WAITING, registration);
            }
          };
        };
      })
      .catch(() => {
        /* silent — no PWA is still a working app */
      });
  });
};

/** Ask the waiting SW (if any) to skipWaiting and take over immediately. */
export const activateWaitingWorker = async () => {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (reg?.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
};
